import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import { useThunk } from "../../hooks/useThunk";
import {
	getRemixerUserById,
	getRemixerUserTracks,
	getRemixerUserEarnings,
	getRemixerUploadedTracks,
	getRemixerEarningsDetails,
	getRemixerEarningsSummary,
} from "../../services/thunks/remixerUserThunks";

import showToast from "../../utils/toastNotifications";
import { formStyles } from "../../components/Form/FormUtils";
import Input from "../../components/Form/Input";
import Table from "../../components/Table/Table";
import { formatJoinedDate } from "../../utils/dateFormating";
import { roles } from "../../constants/commonConstants";
import CountUp from "react-countup";

const ViewRemixerUser = () => {
	const auth = useSelector((state) => state.auth);
	const params = useParams();
	const id = auth.role === roles.REMIXER ? auth.user?._id : params.id;
	const navigate = useNavigate();
	const { role } = auth;
	const outletContext = useOutletContext();
  const setTitle = outletContext?.setTitle || null;
	const tracksTableRef = useRef(null);
	const earningsTableRef = useRef(null);
	const tracksPageSetterRef = useRef(null);
	const earningsPageSetterRef = useRef(null);
	const isRemixer = role === roles.REMIXER;

	const [loading, setLoading] = useState(true);
	const [remixerUser, setRemixerUser] = useState(null);
	const [monthlyEarnings, setMonthlyEarnings] = useState([]);
	const [totalEarnings, setTotalEarnings] = useState(0);
	const [totalDownloads, setTotalDownloads] = useState(0);
	const [averageRate, setAverageRate] = useState(0);
	const [monthsActive, setMonthsActive] = useState(0);
	const [refreshTrigger, setRefreshTrigger] = useState(0);
	const [highlightedRowId, setHighlightedRowId] = useState(null);

	const [doGetRemixerUserById] = useThunk(getRemixerUserById);
	const [doGetRemixerUserTracks] = useThunk(getRemixerUserTracks);
	const [doGetRemixerUserEarnings] = useThunk(getRemixerUserEarnings);
	const [doGetRemixerEarningsSummary] = useThunk(getRemixerEarningsSummary);

	useEffect(() => {
		loadRemixerUserData();
	}, [id]);

	// Update title when remixer data is loaded
useEffect(() => {
    if (remixerUser) {
      const remixerName = remixerUser.remixerName || remixerUser.remixerProfileId || "Unknown";
      const newTitle = `Remixer Overview - ${remixerName}`;
      console.log('ViewRemixerUser - Setting title to:', newTitle);
      
      if (setTitle) {
        setTitle(newTitle);
      } else {
        console.warn('ViewRemixerUser - setTitle function not available');
      }
    }
  }, [remixerUser, setTitle]);

	const loadRemixerUserData = async () => {
		try {
			setLoading(true);

			// Load user basic info
			const userResult = await doGetRemixerUserById({
				token: auth.token,
				id: id,
			});

			if (userResult.success) {
				setRemixerUser(userResult.response.data);
				 if (setTitle) {
        const remixerName = userResult.response.data.remixerName || userResult.response.data.remixerProfileId || "Unknown";
        setTitle(`Remixer Overview - ${remixerName}`);
      }
			} else {
				showToast("error", "Failed to load remixer user data");
				navigate(-1);
				return;
			}

			// Load earnings data for summary (you might want to get all data here for summary)
			const earningsResult = await doGetRemixerEarningsSummary({
				token: auth.token,
				id: id,
			});

			if (earningsResult.success) {
				const result =
					earningsResult.response.data?.items ||
					earningsResult.response.data ||
					[];
				setMonthlyEarnings(result);
				setTotalEarnings(result.totalEarnings || 0);
				setTotalDownloads(result.totalDownloads || 0);
				setAverageRate(result.payoutRate || 0);
				setMonthsActive(result.activeMonths || 0);
			}
		} catch (error) {
			showToast("error", "Failed to load remixer user data");
			navigate(-1);
		} finally {
			setLoading(false);
		}
	};

	const handleEdit = () => {
		navigate(`/remixer-user/edit/${id}`);
	};

	const handleBack = () => {
		navigate(-1);
	};

	const formatCurrency = (amount) => {
		return `$${(amount || 0).toFixed(2)}`;
	};

	// Table columns for uploaded tracks
	const tracksTableColumns = useMemo(
		() => [
			{
				title: (
					<div style={{ paddingLeft: "2.375rem" }}>Track Title</div>
				),
				dataIndex: "title",
				key: "title",
				minWidth: "200px",
				render: (text) => (
					<div className="min-h-[32px] flex items-center text-sm font-[400] font-public-sans text-[#E2E2E2] pl-[2.375rem]">
						{text || "N/A"}
					</div>
				),
			},
			{
				title: "Version Type",
				dataIndex: "versionType",
				key: "versionType",
				minWidth: "150px",
				render: (text) => (
					<div className="min-h-[32px] flex items-center text-sm font-[400] font-public-sans text-[#E2E2E2]">
						{text || "N/A"}
					</div>
				),
			},
			{
				title: "Upload Date",
				dataIndex: "uploadDate",
				key: "uploadDate",
				minWidth: "150px",
				render: (date) => (
					<div className="min-h-[32px] flex items-center text-sm font-[400] font-public-sans text-[#E2E2E2]">
						{formatJoinedDate(date)}
					</div>
				),
			},
			{
				title: "Downloads",
				dataIndex: "downloads",
				key: "downloads",
				align: "center",
				minWidth: "150px",
				render: (count) => (
					<div className="min-h-[32px] flex items-center text-sm font-[400] font-public-sans text-[#E2E2E2]">
						{count || 0}
					</div>
				),
			},
		],
		[]
	);

	// Table columns for monthly earnings
	const earningsTableColumns = useMemo(
		() => [
			{
				title: <div style={{ paddingLeft: "2.375rem" }}>Month</div>,
				dataIndex: "month",
				key: "month",
				minWidth: "150px",
				render: (text) => (
					<div className="min-h-[32px] flex items-center text-sm font-[400] font-public-sans text-[#E2E2E2] pl-[2.375rem]">
						{text || "N/A"}
					</div>
				),
			},
			{
				title: "Downloads",
				dataIndex: "downloads",
				key: "downloads",
				minWidth: "150px",
				render: (count) => (
					<div className="min-h-[32px] flex items-center text-sm font-[400] font-public-sans text-[#E2E2E2]">
						{count || 0}
					</div>
				),
			},
			{
				title: "Payout Rate",
				dataIndex: "payoutRate",
				key: "payoutRate",
				minWidth: "150px",
				render: (rate) => (
					<div className="min-h-[32px] flex items-center text-sm font-[400] font-public-sans text-[#E2E2E2]">
						{formatCurrency(rate)}
					</div>
				),
			},
			{
				title: "Monthly Earnings",
				dataIndex: "earnings",
				key: "earnings",
				minWidth: "150px",
				render: (amount) => (
					<div className="min-h-[32px] flex items-center text-sm font-[400] font-public-sans text-[#E2E2E2]">
						{formatCurrency(amount)}
					</div>
				),
			},
		],
		[]
	);

	// Fixed fetch function for earnings table that properly handles pagination
	const fetchEarningsData = async (params) => {
		try {
			const result = await doGetRemixerUserEarnings({
				token: auth.token,
				id: id,
				offset: params.offset || 0,
				limit: params.limit || 10,
				...params, // Include any other parameters like sorting
			});

			if (result.success) {
				const responseData = result.response.data;

				// Handle different response structures
				if (Array.isArray(responseData)) {
					// If response.data is directly an array
					return {
						success: true,
						response: {
							data: {
								items: responseData,
								pagination: {
									totalDocuments: responseData.length,
									currentPage:
										Math.floor(
											(params.offset || 0) /
												(params.limit || 10)
										) + 1,
									totalPages: Math.ceil(
										responseData.length /
											(params.limit || 10)
									),
								},
							},
						},
					};
				} else if (responseData && responseData.items) {
					// If response has proper pagination structure
					return result;
				} else {
					// Handle case where data structure is different
					return {
						success: true,
						response: {
							data: {
								items: [],
								pagination: {
									totalDocuments: 0,
									currentPage: 1,
									totalPages: 1,
								},
							},
						},
					};
				}
			} else {
				return {
					success: false,
					response: {
						data: {
							items: [],
							pagination: {
								totalDocuments: 0,
								currentPage: 1,
								totalPages: 1,
							},
						},
					},
				};
			}
		} catch (error) {
			console.error("Error fetching earnings data:", error);
			return {
				success: false,
				response: {
					data: {
						items: [],
						pagination: {
							totalDocuments: 0,
							currentPage: 1,
							totalPages: 1,
						},
					},
				},
			};
		}
	};

	if (loading) {
		return (
			<div className="px-6 py-10 text-white min-h-screen flex items-center justify-center">
				<div className="text-lg">Loading...</div>
			</div>
		);
	}

	if (!remixerUser) {
		return (
			<div className="px-6 py-10 text-white min-h-screen flex items-center justify-center">
				<div className="text-lg">Remixer user not found</div>
			</div>
		);
	}

	return (
		<div className="px-6 py-10 text-white min-h-screen">
			{/* Remixer Info Section */}
			<div className="bg-[#111111D6] rounded-lg p-6 border border-[#505050] mb-6">
				<h2 className="text-[20px] font-bold font-public-sans mb-6 text-white">
					Remixer Info
				</h2>

				<div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
					{/* Remixer ID */}
					<div>
						<Input
							label="Remixer ID"
							value={remixerUser.remixerId || "N/A"}
							readOnly={true}
							inputStyle={formStyles.remixerApplicationInput}
							labelStyle={formStyles.remixerApplicationlabel}
							className="cursor-default"
						/>
					</div>

					{/* Remixer Name */}
					<div>
						<Input
							label="Remixer Name"
							value={
								remixerUser.remixerName ||
								remixerUser.remixerProfileId ||
								"N/A"
							}
							readOnly={true}
							inputStyle={formStyles.remixerApplicationInput}
							labelStyle={formStyles.remixerApplicationlabel}
							className="cursor-default"
						/>
					</div>

					{/* Email Address */}
					<div>
						<Input
							label="Email Address"
							value={remixerUser.email || "N/A"}
							readOnly={true}
							inputStyle={formStyles.remixerApplicationInput}
							labelStyle={formStyles.remixerApplicationlabel}
							className="cursor-default"
						/>
					</div>

					{/* Contact Number */}
					<div>
						<Input
							label="Contact Number"
							value={remixerUser.contactNumber || "N/A"}
							readOnly={true}
							inputStyle={formStyles.remixerApplicationInput}
							labelStyle={formStyles.remixerApplicationlabel}
							className="cursor-default"
						/>
					</div>

					{/* Created On */}
					<div>
						<Input
							label="Created On"
							value={formatJoinedDate(remixerUser.createdAt)}
							readOnly={true}
							inputStyle={formStyles.remixerApplicationInput}
							labelStyle={formStyles.remixerApplicationlabel}
							className="cursor-default"
						/>
					</div>

					{/* Status */}
					<div>
						<Input
							label="Status"
							value={remixerUser.status || "N/A"}
							readOnly={true}
							inputStyle={formStyles.remixerApplicationInput}
							labelStyle={formStyles.remixerApplicationlabel}
							className="cursor-default"
						/>
					</div>
				</div>
			</div>

			{/* Uploaded Tracks Section */}
			<div className="bg-[#111111D6] rounded-lg p-6 border border-[#505050] mb-6">
				<h2 className="text-[20px] font-bold font-public-sans mb-6 text-white">
					Uploaded Tracks
				</h2>

				<div ref={tracksTableRef}>
					<Table
						columns={tracksTableColumns}
						fetchData={getRemixerUploadedTracks}
						refreshTrigger={refreshTrigger}
						headingBgColor="#0D141B"
						headingTextColor="#FFFFFF"
						customQueryParams={(params) => ({
							...params,
							token: auth.token,
							id: id,
						})}
						highlightedRowId={highlightedRowId}
						setPageCallback={(fn) =>
							(tracksPageSetterRef.current = fn)
						}
						useLoadMore={true}
						//initialPageSize={5}
					/>
				</div>
			</div>

			{/* Monthly Earnings Section */}
			<div className="bg-[#111111D6] rounded-lg p-6 border border-[#505050] mb-6">
				<h2 className="text-[20px] font-bold font-public-sans mb-6 text-white">
					Monthly Earnings
				</h2>

				{/* Summary Cards */}
				<div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
					<div className="flex flex-col justify-center items-center text-center bg-[#5BAFD70A] h-[5.625rem]">
						<div className="text-[#5BAFD7] text-sm font-public-sans mb-1">
							Total Earnings
						</div>
						<div className="text-white text-xl font-bold font-public-sans">
							<CountUp
								end={totalEarnings}
								decimals={2}
								duration={5}
								separator=","
								prefix="$"
								preserveValue
								useEasing
							/>
						</div>
					</div>
					<div className="flex flex-col justify-center items-center text-center bg-[#5BAFD70A] h-[5.625rem]">
						<div className="text-[#5BAFD7] text-sm font-public-sans mb-1">
							Total Downloads
						</div>
						<div className="text-white text-xl font-bold font-public-sans">
							<CountUp
								end={totalDownloads}
								duration={5}
								separator=","
								preserveValue
								useEasing
							/>
						</div>
					</div>
					<div className="flex flex-col justify-center items-center text-center bg-[#5BAFD70A] h-[5.625rem]">
						<div className="text-[#5BAFD7] text-sm font-public-sans mb-1">
							Average Rate
						</div>
						<div className="text-white text-xl font-bold font-public-sans">
							<CountUp
								end={averageRate}
								decimals={2}
								duration={5}
								separator=","
								prefix="$"
								preserveValue
								useEasing
							/>
						</div>
					</div>
					<div className="flex flex-col justify-center items-center text-center bg-[#5BAFD70A] h-[5.625rem]">
						<div className="text-[#5BAFD7] text-sm font-public-sans mb-1">
							Months Active
						</div>
						<div className="text-white text-xl font-bold font-public-sans">
							<CountUp
								end={monthsActive}
								duration={5}
								preserveValue
								useEasing
							/>
						</div>
					</div>
				</div>

				{/* Monthly Earnings Table */}
				<div ref={earningsTableRef}>
					<Table
						columns={earningsTableColumns}
						fetchData={getRemixerEarningsDetails}
						refreshTrigger={refreshTrigger}
						headingBgColor="#0D141B"
						headingTextColor="#FFFFFF"
						customQueryParams={(params) => ({
							...params,
							token: auth.token,
							id: id,
						})}
						highlightedRowId={highlightedRowId}
						setPageCallback={(fn) =>
							(earningsPageSetterRef.current = fn)
						}
						useLoadMore={true}
						initialPageSize={5}
					/>
				</div>
			</div>
		</div>
	);
};

export default ViewRemixerUser;
